// import { Component } from '@theme/component';
// import { onDocumentReady, changeMetaThemeColor } from '@theme/utilities';

// /**
//  * @typedef {Object} HeaderComponentRefs
//  * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
//  * @property {HTMLElement} headerMenu - The header menu element
//  * @property {HTMLElement} headerRowTop - The header top row element
//  */

// /**
//  * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
//  */

// /**
//  * A custom element that manages the site header.
//  *
//  * @extends {Component<HeaderComponentRefs>}
//  */

// class HeaderComponent extends Component {
//   requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

//   /**
//    * Width of window when header drawer was hidden
//    * @type {number | null}
//    */
//   #menuDrawerHiddenWidth = null;

//   /**
//    * An intersection observer for monitoring sticky header position
//    * @type {IntersectionObserver | null}
//    */
//   #intersectionObserver = null;

//   /**
//    * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
//    * @type {boolean}
//    */
//   #offscreen = false;

//   /**
//    * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
//    * @type {number}
//    */
//   #lastScrollTop = 0;

//   /**
//    * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
//    * @type {number | null}
//    */
//   #timeout = null;

//   /**
//    * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
//    * @constant {number}
//    */
//   #animationDelay = 150;

//   /**
//    * Keeps the global `--header-height` custom property up to date,
//    * which other theme components can then consume
//    */
//   #resizeObserver = new ResizeObserver(([entry]) => {
//     if (!entry) return;

//     const { height } = entry.target.getBoundingClientRect();
//     document.body.style.setProperty('--header-height', `${height}px`);

//     // Check if the menu drawer should be hidden in favor of the header menu
//     if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
//       this.#updateMenuVisibility(false);
//     }
//   });

//   /**
//    * Observes the header while scrolling the viewport to track when its actively sticky
//    * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
//    */
//   #observeStickyPosition = (alwaysSticky = true) => {
//     if (this.#intersectionObserver) return;

//     const config = {
//       threshold: alwaysSticky ? 1 : 0,
//     };

//     this.#intersectionObserver = new IntersectionObserver(([entry]) => {
//       if (!entry) return;

//       const { isIntersecting } = entry;

//       if (alwaysSticky) {
//         this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
//         changeMetaThemeColor(this.refs.headerRowTop);
//       } else {
//         this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
//       }
//     }, config);

//     this.#intersectionObserver.observe(this);
//   };

//   /**
//    * Handles the overflow minimum event from the header menu
//    * @param {OverflowMinimumEvent} event
//    */
//   #handleOverflowMinimum = (event) => {
//     this.#updateMenuVisibility(event.detail.minimumReached);
//   };

//   /**
//    * Updates the visibility of the menu and drawer
//    * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
//    */
//   #updateMenuVisibility(hideMenu) {
//     if (hideMenu) {
//       this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
//       this.#menuDrawerHiddenWidth = window.innerWidth;
//       this.refs.headerMenu.classList.add('hidden');
//     } else {
//       this.refs.headerDrawerContainer.classList.add('desktop:hidden');
//       this.#menuDrawerHiddenWidth = null;
//       this.refs.headerMenu.classList.remove('hidden');
//     }
//   }

//   #handleWindowScroll = () => {
//     const stickyMode = this.getAttribute('sticky');
//     if (!this.#offscreen && stickyMode !== 'always') return;

//     const scrollTop = document.scrollingElement?.scrollTop ?? 0;
//     const isScrollingUp = scrollTop < this.#lastScrollTop;
//     if (this.#timeout) {
//       clearTimeout(this.#timeout);
//       this.#timeout = null;
//     }

//     if (scrollTop > 0) {
//       this.dataset.stickyState = 'active';
//     } else {
//       this.dataset.stickyState = 'inactive';
//     }

//     if (stickyMode === 'always') {
//       if (isScrollingUp) {
//         if (this.getBoundingClientRect().top >= 0) {
//           this.dataset.scrollDirection = 'none';
//         } else {
//           this.dataset.scrollDirection = 'up';
//         }
//       } else {
//         this.dataset.scrollDirection = 'down';
//       }

//       this.#lastScrollTop = scrollTop;
//       return;
//     }

//     if (isScrollingUp) {
//       this.removeAttribute('data-animating');

//       if (this.getBoundingClientRect().top >= 0) {
//         // reset sticky state when header is scrolled up to natural position
//         this.#offscreen = false;
//         this.dataset.stickyState = 'inactive';
//         this.dataset.scrollDirection = 'none';
//       } else {
//         // show sticky header when scrolling up
//         this.dataset.stickyState = 'active';
//         this.dataset.scrollDirection = 'up';
//       }
//     } else if (this.dataset.stickyState === 'active') {
//       this.dataset.scrollDirection = 'none';
//       // delay transitioning to idle hidden state for hiding animation
//       this.setAttribute('data-animating', '');

//       this.#timeout = setTimeout(() => {
//         this.dataset.stickyState = 'idle';
//         this.removeAttribute('data-animating');
//       }, this.#animationDelay);
//     } else {
//       this.dataset.scrollDirection = 'none';
//       this.dataset.stickyState = 'idle';
//     }

//     this.#lastScrollTop = scrollTop;
//   };

//   connectedCallback() {
//     super.connectedCallback();
//     this.#resizeObserver.observe(this);
//     this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

//     const stickyMode = this.getAttribute('sticky');
//     if (stickyMode) {
//       this.#observeStickyPosition(stickyMode === 'always');

//       if (stickyMode === 'scroll-up' || stickyMode === 'always') {
//         document.addEventListener('scroll', this.#handleWindowScroll);
//       }
//     }
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();
//     this.#resizeObserver.disconnect();
//     this.#intersectionObserver?.disconnect();
//     this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
//     document.removeEventListener('scroll', this.#handleWindowScroll);
//     document.body.style.setProperty('--header-height', '0px');
//   }
// }

// if (!customElements.get('header-component')) {
//   customElements.define('header-component', HeaderComponent);
// }

// onDocumentReady(() => {
//   const header = document.querySelector('#header-component');
//   const headerGroup = document.querySelector('#header-group');

//   // Update header group height on resize of any child
//   if (headerGroup) {
//     const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

//     // Observe all children of the header group
//     const children = headerGroup.children;
//     for (let i = 0; i < children.length; i++) {
//       const element = children[i];
//       if (element === header || !(element instanceof HTMLElement)) continue;
//       resizeObserver.observe(element);
//     }

//     // Also observe the header group itself for child changes
//     const mutationObserver = new MutationObserver((mutations) => {
//       for (const mutation of mutations) {
//         if (mutation.type === 'childList') {
//           // Re-observe all children when the list changes
//           const children = headerGroup.children;
//           for (let i = 0; i < children.length; i++) {
//             const element = children[i];
//             if (element === header || !(element instanceof HTMLElement)) continue;
//             resizeObserver.observe(element);
//           }
//         }
//       }
//     });

//     mutationObserver.observe(headerGroup, { childList: true });
//   }

//   // Handle announcement bar height for sticky positioning
//   const announcementBar = document.querySelector('.announcement-bar');
//   if (announcementBar && announcementBar instanceof HTMLElement) {
//     const updateAnnouncementBarHeight = () => {
//       const height = announcementBar.offsetHeight;
//       document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
//     };

//     // Initial calculation
//     updateAnnouncementBarHeight();

//     // Observe announcement bar for size changes
//     const announcementBarResizeObserver = new ResizeObserver(updateAnnouncementBarHeight);
//     announcementBarResizeObserver.observe(announcementBar);
//   }
// });
import { Component } from '@theme/component';
import { changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer
 * @property {HTMLElement} headerMenu
 * @property {HTMLElement} headerRowTop
 */

/**
 * @typedef {Object} AdjustMenuDrawerState
 * @property {number | null} maxVH
 * @property {number | null} lastVH
 */

/** @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #menuDrawerHiddenWidth = null;
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  #offscreen = false;
  #lastScrollTop = 0;
  /** @type {number | null} */
  #timeout = null;
  #animationDelay = 150;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;
    if (document.documentElement.classList.contains('header-v2-active')) return;
    if (this.closest('.header-ab-test-control') && getComputedStyle(this.closest('.header-ab-test-control')).display === 'none') {
      return;
    }
    const height = entry.target.getBoundingClientRect().height;
    document.body.style.setProperty('--header-height', `${height}px`);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  #observeStickyPosition(alwaysSticky = true) {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const isSticky = !entry.isIntersecting;

      if (alwaysSticky) {
        this.dataset.stickyState = isSticky ? 'active' : 'inactive';
        if (this.refs.headerRowTop) {
          changeMetaThemeColor(this.refs.headerRowTop);
        }
      } else {
        this.#offscreen = isSticky || this.dataset.stickyState === 'active';
      }
    }, { threshold: alwaysSticky ? 1 : 0 });

    if (this) {
      this.#intersectionObserver.observe(this);
    }
  }

  /**
   * @param {CustomEvent<{ minimumReached: boolean }>} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * @param {boolean} hideMenu
   */
  #updateMenuVisibility(hideMenu) {
    const drawer = this.refs.headerDrawerContainer;
    const menu = this.refs.headerMenu;

    if (!drawer || !menu) return;

    if (hideMenu) {
      drawer.classList.remove('desktop:hidden');
      menu.classList.add('hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      drawer.classList.add('desktop:hidden');
      menu.classList.remove('hidden');
      this.#menuDrawerHiddenWidth = null;
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    this.dataset.stickyState = scrollTop > 0 ? 'active' : 'inactive';

    if (stickyMode === 'always') {
      this.dataset.scrollDirection = isScrollingUp
        ? this.getBoundingClientRect().top >= 0 ? 'none' : 'up'
        : 'down';
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');
      if (this.getBoundingClientRect().top >= 0) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.setAttribute('data-animating', '');
      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    // Set default state to prevent flicker
    this.dataset.stickyState = 'inactive';
    this.dataset.scrollDirection = 'none';

    requestAnimationFrame(() => {
      const stickyMode = this.getAttribute('sticky');
      if (stickyMode) {
        this.#observeStickyPosition(stickyMode === 'always');

        if (['scroll-up', 'always'].includes(stickyMode)) {
          this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
          document.addEventListener('scroll', this.#handleWindowScroll);
          this.#handleWindowScroll(); // Set initial sticky state
        }
      }
    });

    const headerGroup = this.closest('#header-group');
    if (headerGroup) {
      const resizeObserver = new ResizeObserver(() => {
        if (typeof calculateHeaderGroupHeight === 'function') {
          calculateHeaderGroupHeight(this, headerGroup);
        }
      });

      Array.from(headerGroup.children).forEach((el) => {
        if (el !== this && el instanceof HTMLElement) {
          resizeObserver.observe(el);
        }
      });

      new MutationObserver((mutations) => {
        if (mutations.some(m => m.type === 'childList')) {
          Array.from(headerGroup.children).forEach((el) => {
            if (el !== this && el instanceof HTMLElement) {
              resizeObserver.observe(el);
            }
          });
        }
      }).observe(headerGroup, { childList: true });
    }

    const announcementBar = document.querySelector('.announcement-bar');
    if (announcementBar instanceof HTMLElement) {
      const updateHeight = () => {
        if (document.documentElement.classList.contains('header-v2-active')) {
          document.documentElement.style.setProperty('--announcement-bar-height', '0px');
          return;
        }
        if (announcementBar.dataset.sticky === 'true') {
          document.documentElement.style.setProperty('--announcement-bar-height', `${announcementBar.offsetHeight}px`);
        } else {
          document.documentElement.style.setProperty('--announcement-bar-height', '0px');
        }
      };
      updateHeight();
      new ResizeObserver(updateHeight).observe(announcementBar);
    }

    // Calculate and update the actual position of the first header row's bottom edge
    // This accounts for ANY elements above it (app banners, announcement bars, etc.)
    const updateHeaderPosition = () => {
      // Target the first row specifically (header__row--top)
      const firstRow = this.querySelector('.header__row--top') || this.querySelector('.header__row');
      
      if (firstRow) {
        const rect = firstRow.getBoundingClientRect();
        const rowBottom = rect.bottom;
        document.documentElement.style.setProperty('--header-bottom-position', `${rowBottom}px`);
      } else {
        // Fallback to entire header if no row found
        const rect = this.getBoundingClientRect();
        document.documentElement.style.setProperty('--header-bottom-position', `${rect.bottom}px`);
      }
    };

    // Update on resize, scroll, and when elements above header change
    updateHeaderPosition();
    new ResizeObserver(updateHeaderPosition).observe(this);
    window.addEventListener('scroll', updateHeaderPosition);
    
    // Also observe any changes to elements that might be above the header
    const headerObserver = new MutationObserver(updateHeaderPosition);
    headerObserver.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    if (this.closest('.header-ab-test-control')) {
      const syncHeaderV2State = () => {
        if (!document.documentElement.classList.contains('header-v2-active')) return;

        document.documentElement.style.setProperty('--announcement-bar-height', '0px');
        document.body.style.setProperty('--header-height', '0px');
      };

      syncHeaderV2State();

      new MutationObserver(syncHeaderV2State).observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

/** @type {AdjustMenuDrawerState} */
const menuDrawerState = {
  maxVH: null,
  lastVH: null
};

function adjustMenuDrawer() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (!isIOS) return;

  const footer = document.querySelector('.menu-drawer__sticky-footer');
  const menuDrawer = document.querySelector('.menu-drawer');
  
  if (!footer || !menuDrawer) return;

  // Cast to HTMLElement for style and classList access
  const footerElement = footer instanceof HTMLElement ? footer : null;
  const menuDrawerElement = menuDrawer instanceof HTMLElement ? menuDrawer : null;
  
  if (!footerElement || !menuDrawerElement) return;

  // Detect browser type for different handling
  const isChrome = /CriOS/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  
  // Use different viewport detection methods based on browser
  let vh;
  if (window.visualViewport) {
    vh = window.visualViewport.height;
  } else {
    vh = window.innerHeight;
  }
  
  // Additional detection for Chrome on iOS
  if (isChrome && window.screen && window.screen.height) {
    const screenHeight = window.screen.height;
    const windowHeight = window.innerHeight;
    const chromeAddressBarHeight = screenHeight - windowHeight;
    
    // If there's a significant difference, address bar is likely visible
    if (chromeAddressBarHeight > 50) {
      vh = windowHeight;
      // Force initial state detection for Chrome
      if (!menuDrawerState.maxVH) {
        menuDrawerState.maxVH = screenHeight;
      }
    }
  }
  
  if (!menuDrawerState.maxVH) {
    menuDrawerState.maxVH = vh;
    menuDrawerState.lastVH = vh;
  }

  if (vh > menuDrawerState.maxVH) {
    menuDrawerState.maxVH = vh;
  }

  const heightDifference = menuDrawerState.maxVH - vh;
  
  // Different thresholds for different browsers
  let addressBarVisible;
  if (isChrome) {
    // Chrome on iOS might need a lower threshold
    addressBarVisible = heightDifference > 20;
  } else {
    // Safari threshold
    addressBarVisible = heightDifference > 30;
  }

  const lastVH = menuDrawerState.lastVH || vh;
  menuDrawerState.lastVH = vh;

  // Different sensitivity for different browsers
  const sensitivity = isChrome ? 5 : 10;
  if (Math.abs(vh - lastVH) < sensitivity) return;

  // Different positioning values for different browsers
  let footerBottom, menuPadding;
  
  if (addressBarVisible) {
    if (isChrome) {
      // Chrome might need more aggressive positioning
      footerBottom = '80px';
      menuPadding = '180px';
    } else {
      footerBottom = '70px';
      menuPadding = '170px';
    }
    
    footerElement.style.bottom = footerBottom;
    menuDrawerElement.style.paddingBottom = menuPadding;
    
    footerElement.classList.add('address-bar-visible');
    menuDrawerElement.classList.add('address-bar-visible');
    
    // Add browser-specific class for CSS targeting
    if (isChrome) {
      footerElement.classList.add('chrome-ios');
      menuDrawerElement.classList.add('chrome-ios');
    } else if (isSafari) {
      footerElement.classList.add('safari-ios');
      menuDrawerElement.classList.add('safari-ios');
    }
  } else {
    if (isChrome) {
      footerBottom = '25px';
      menuPadding = '125px';
    } else {
      footerBottom = '20px';
      menuPadding = '120px';
    }
    
    footerElement.style.bottom = footerBottom;
    menuDrawerElement.style.paddingBottom = menuPadding;
    
    footerElement.classList.remove('address-bar-visible');
    menuDrawerElement.classList.remove('address-bar-visible');
    
    // Remove browser-specific classes
    footerElement.classList.remove('chrome-ios', 'safari-ios');
    menuDrawerElement.classList.remove('chrome-ios', 'safari-ios');
  }

  if (window.location.search.includes('debug=menu')) {
    console.log('Menu Drawer Adjustment:', {
      browser: isChrome ? 'Chrome' : isSafari ? 'Safari' : 'Other',
      currentHeight: vh,
      maxHeight: menuDrawerState.maxVH,
      heightDifference,
      addressBarVisible,
      footerBottom: footerElement.style.bottom,
      menuPadding: menuDrawerElement.style.paddingBottom
    });
  }
}

function setupMenuDrawerAdjustments() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (!isIOS) return;

  // Initial adjustment with delay to ensure proper detection
  setTimeout(adjustMenuDrawer, 100);

  /** @type {number | undefined} */
  let adjustTimeout;
  const debouncedAdjust = () => {
    if (adjustTimeout) {
      clearTimeout(adjustTimeout);
    }
    adjustTimeout = setTimeout(adjustMenuDrawer, 50);
  };

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', debouncedAdjust);
    window.visualViewport.addEventListener('scroll', debouncedAdjust);
  }

  window.addEventListener('resize', debouncedAdjust);
  window.addEventListener('scroll', debouncedAdjust);
  
  window.addEventListener('orientationchange', () => {
    menuDrawerState.maxVH = null;
    menuDrawerState.lastVH = null;
    
    setTimeout(adjustMenuDrawer, 300);
  });

  const setupDrawerEvents = () => {
    const drawerDetails = document.getElementById('Details-menu-drawer-container');
    if (drawerDetails && drawerDetails instanceof HTMLDetailsElement) {
      drawerDetails.addEventListener('toggle', () => {
        if (drawerDetails.open) {
          setTimeout(adjustMenuDrawer, 100);
        }
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupDrawerEvents);
  } else {
    setupDrawerEvents();
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      setTimeout(adjustMenuDrawer, 100);
    }
  });
}

window.addEventListener('load', setupMenuDrawerAdjustments);
document.addEventListener('DOMContentLoaded', setupMenuDrawerAdjustments);
